/**
 * Push-notification subscription helper.
 *
 * Orchestrates the browser Push API:
 *   1. fetch the VAPID public key from the server
 *   2. ask for notification permission if needed
 *   3. call `pushManager.subscribe()` with the VAPID key
 *   4. POST the resulting subscription to the server so /send fan-outs reach
 *      this device even when the tab is closed
 *
 * Every method is injectable so this can be fully unit-tested without
 * touching the real browser push stack.
 */
import { fromBase64Url, type DeviceId, type UserId } from '@chat/protocol';
import type { ChatClient } from './api.js';

export type NotificationPermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

export interface PushDeps {
  readonly client: ChatClient;
  readonly token: string;
  readonly userId: UserId;
  readonly deviceId: DeviceId;
  readonly registrationProvider?: () => Promise<ServiceWorkerRegistration | undefined>;
  readonly permissionRequester?: () => Promise<NotificationPermission>;
  readonly swUrl?: string;
}

async function defaultRegistration(swUrl: string): Promise<ServiceWorkerRegistration | undefined> {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return undefined;
  const existing = await navigator.serviceWorker.getRegistration(swUrl);
  if (existing) return existing;
  return navigator.serviceWorker.register(swUrl);
}

async function defaultRequestPermission(): Promise<NotificationPermission> {
  if (typeof Notification === 'undefined') return 'denied';
  if (Notification.permission === 'granted') return 'granted';
  return Notification.requestPermission();
}

export class PushService {
  readonly #deps: Required<Omit<PushDeps, 'swUrl'>> & { swUrl: string };

  constructor(deps: PushDeps) {
    const swUrl = deps.swUrl ?? '/sw.js';
    this.#deps = {
      ...deps,
      swUrl,
      registrationProvider: deps.registrationProvider ?? (() => defaultRegistration(swUrl)),
      permissionRequester: deps.permissionRequester ?? defaultRequestPermission,
    };
  }

  static permissionState(): NotificationPermissionState {
    if (typeof Notification === 'undefined') return 'unsupported';
    return Notification.permission;
  }

  /**
   * Full subscribe flow. Resolves to `'granted'` on success, or the reason
   * we couldn't subscribe. Does not throw — any error surfaces as an
   * `unsupported`/`denied` outcome with the reason exposed on `detail`.
   */
  async subscribe(): Promise<{
    status: 'granted' | 'denied' | 'unsupported';
    detail?: string;
  }> {
    const permission = await this.#deps.permissionRequester();
    if (permission !== 'granted') {
      return { status: permission === 'denied' ? 'denied' : 'unsupported' };
    }
    const registration = await this.#deps.registrationProvider();
    if (!registration?.pushManager) {
      return { status: 'unsupported', detail: 'no push manager' };
    }
    const vapid = await this.#deps.client.fetchVapidPublicKey();
    const applicationServerKey = fromBase64Url(vapid);
    let subscription: PushSubscription | null = null;
    try {
      subscription = await registration.pushManager.getSubscription();
    } catch {
      subscription = null;
    }
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: new Uint8Array(applicationServerKey).buffer,
      });
    }
    const serialized = serialize(subscription, this.#deps.userId, this.#deps.deviceId);
    await this.#deps.client.registerPushSubscription(this.#deps.token, serialized);
    return { status: 'granted' };
  }

  async unsubscribe(): Promise<void> {
    const registration = await this.#deps.registrationProvider();
    const existing = await registration?.pushManager.getSubscription();
    await existing?.unsubscribe();
    await this.#deps.client.removePushSubscription(this.#deps.token);
  }
}

function serialize(
  sub: PushSubscription,
  userId: UserId,
  deviceId: DeviceId,
): {
  userId: UserId;
  deviceId: DeviceId;
  endpoint: string;
  p256dh: string;
  auth: string;
  registeredAt: number;
} {
  const p256dh = sub.getKey('p256dh');
  const auth = sub.getKey('auth');
  if (!p256dh || !auth) {
    throw new Error('subscription is missing p256dh/auth keys');
  }
  return {
    userId,
    deviceId,
    endpoint: sub.endpoint,
    p256dh: toBase64UrlFromBuffer(p256dh),
    auth: toBase64UrlFromBuffer(auth),
    registeredAt: Date.now(),
  };
}

function toBase64UrlFromBuffer(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
